import { useRef } from 'react';

import type { UserPreferences, UserPreferencesProvider } from '@atlaskit/editor-common/types';

import { getApolloClient } from '@confluence/graphql';
import { useSessionData } from '@confluence/session-data';
import { fg } from '@confluence/feature-gating';

import { EditorPreferencesMutation } from './EditorPreferencesMutation.graphql';
import { EditorPreferencesQuery } from './EditorPreferencesQuery.graphql';
import type {
	EditorPreferencesMutation as EditorPreferencesMutationResponse,
	EditorPreferencesMutationVariables,
} from './__types__/EditorPreferencesMutation';
import type { EditorPreferencesQuery as EditorPreferencesQueryType } from './__types__/EditorPreferencesQuery';
import { EditorUserPreferencesProvider } from './EditorUserPreferencesProvider';

export const parseConfluenceEditorSettings = (confluenceEditorSettings?: any) => {
	if (confluenceEditorSettings && (confluenceEditorSettings satisfies UserPreferences)) {
		return confluenceEditorSettings as UserPreferences;
	}

	return {} as UserPreferences;
};

export class ConfluenceEditorPreferencesProvider implements UserPreferencesProvider {
	private userPreferences: UserPreferences = {};
	// We use getApolloClient to get the apollo client instance
	// This is to avoid adding required apollo context
	private apolloClient = getApolloClient();
	private initialized = false;

	public constructor() {
		this.init();
	}

	public init() {
		if (!this.initialized) {
			this.fetchPreferences().catch(() => {
				// Do nothing
			});
		}
	}

	// add this to implment the UserPreferensProvider interface.
	public loadPreferences(): Promise<UserPreferences> {
		return Promise.reject();
	}

	private async fetchPreferences(): Promise<void> {
		if (!this.apolloClient) {
			return Promise.reject('No apollo client');
		}

		try {
			if (!this.initialized) {
				const { data } = await this.apolloClient.query<EditorPreferencesQueryType>({
					query: EditorPreferencesQuery,
				});
				this.userPreferences = parseConfluenceEditorSettings(
					data?.userPreferences?.confluenceEditorSettings,
				);
				this.initialized = true;
			}
			return Promise.resolve();
		} catch (error) {
			return Promise.reject(error);
		}
	}

	async updatePreference<K extends keyof UserPreferences>(
		key: K,
		value: UserPreferences[K],
	): Promise<void> {
		if (!this.apolloClient) {
			return Promise.reject('No apollo client');
		}

		this.userPreferences[key] = value;

		const variables = {
			[key as keyof EditorPreferencesMutationVariables]: value,
		} as EditorPreferencesMutationVariables;

		try {
			const response = await this.apolloClient.mutate<
				EditorPreferencesMutationResponse,
				EditorPreferencesMutationVariables
			>({
				mutation: EditorPreferencesMutation,
				variables,
				update: (cache) => {
					// this is required to indicate toolbar changes to progressive object header
					cache.writeQuery({
						query: EditorPreferencesQuery,
						data: {
							userPreferences: {
								__typename: 'UserPreferences',
								confluenceEditorSettings: {
									__typename: 'ConfluenceEditorSettings',
									toolbarDockingInitialPosition: value === 'top' ? value : null, // Null is required since apollo cache uses shallow reference equality
								},
							},
						},
					});
				},
			});

			const confluenceEditorSettings = parseConfluenceEditorSettings(
				response.data?.updateUserPreferences?.confluenceEditorSettings,
			);

			this.userPreferences[key] = confluenceEditorSettings[key];

			return Promise.resolve();
		} catch (error) {
			return Promise.reject(error);
		}
	}

	getPreference<K extends keyof UserPreferences>(key: K): UserPreferences[K] | undefined | null {
		return this.userPreferences[key];
	}
}

const createUserPreferencesProvider = (options?: {
	isLivePage?: boolean;
}): UserPreferencesProvider => {
	if (fg('platform_editor_user_preferences_provider_update')) {
		if (fg('platform_editor_controls_patch_6')) {
			const { isLivePage } = options || {};
			const fallbackUserPreferences = {
				toolbarDockingInitialPosition: isLivePage ? 'none' : 'top',
			} as UserPreferences;

			return new EditorUserPreferencesProvider(fallbackUserPreferences);
		}

		return new EditorUserPreferencesProvider();
	}
	return new ConfluenceEditorPreferencesProvider();
};

export const useEditorPreferencesProvider = (options?: { isLivePage?: boolean }) => {
	const { isLoggedIn } = useSessionData();

	const confluenceEditorPreferencesProvider = useRef<UserPreferencesProvider | undefined>(
		isLoggedIn ? createUserPreferencesProvider(options) : undefined,
	);

	return {
		confluenceEditorPreferencesProvider: confluenceEditorPreferencesProvider.current,
	};
};
